<template>
  <div class="rd-month">
    <div v-if="!employee" class="rd-empty">
      <b>{{ $t("report_desktop.pick_worker") }}</b>
    </div>

    <template v-else>
      <div v-if="!compact" class="rd-detail__head">
        <span class="rd-avatar rd-avatar--lg">{{ initials }}</span>
        <div>
          <h2>{{ employee.fullName }}</h2>
          <span class="rd-detail__sub">{{ subtitle }}</span>
        </div>
        <span class="rd-detail__spacer"></span>
        <button type="button" class="rd-btn rd-btn--sm" @click="print">
          <v-icon size="16">mdi-printer-outline</v-icon>
          {{ $t("print") }}
        </button>
      </div>

      <div class="rd-detail__body">
        <div class="rd-month-nav">
          <button
            type="button"
            class="rd-ib rd-ib--sm"
            :aria-label="$t('report_mobile.prev_month')"
            @click="stepMonth(-1)"
          >
            <v-icon size="18">mdi-chevron-left</v-icon>
          </button>
          <div class="rd-month-nav__title" aria-live="polite">
            {{ monthTitle }}
            <small v-if="monthHint">{{ monthHint }}</small>
          </div>
          <button
            type="button"
            class="rd-ib rd-ib--sm"
            :aria-label="$t('report_mobile.month_next')"
            @click="stepMonth(1)"
          >
            <v-icon size="18">mdi-chevron-right</v-icon>
          </button>
        </div>

        <div class="rd-stats rd-stats--pair">
          <div class="rd-kpi rd-kpi--inverse">
            <span class="rd-kpi__label">{{ $t("report_mobile.stat_orders") }}</span>
            <span class="rd-kpi__value">{{ totals.orders }}</span>
          </div>
          <div class="rd-kpi">
            <span class="rd-kpi__label">{{ $t("report_mobile.stat_dishes") }}</span>
            <span class="rd-kpi__value">{{ totals.dishes }}</span>
          </div>
        </div>

        <div v-if="loading" class="rd-skeleton" style="height: 220px"></div>
        <div v-else-if="failed" class="rd-alert" role="alert">
          <v-icon size="22" color="#7a1b13">mdi-alert-outline</v-icon>
          <div>
            <b>{{ $t("report_mobile.load_failed") }}</b>
            <button type="button" class="rd-btn rd-btn--sm rd-state__retry" @click="loadMonth">
              {{ $t("report_mobile.retry") }}
            </button>
          </div>
        </div>

        <template v-else>
          <div class="rd-heat" role="img" :aria-label="$t('report_desktop.month_overview')">
            <span v-for="(weekday, index) in weekdays" :key="`wd-${index}`" class="rd-heat__wd">
              {{ weekday }}
            </span>
            <template v-for="(cell, index) in cells">
              <span v-if="!cell" :key="`blank-${index}`" class="rd-heat__day is-blank"></span>
              <span
                v-else
                :key="cell.key"
                class="rd-heat__day"
                :class="cell.classes"
                :title="cell.title"
              >
                <b>{{ cell.day }}</b>
                <span>{{ cell.text }}</span>
              </span>
            </template>
          </div>

          <label class="rd-live rd-month__all">
            <input v-model="showAllDays" type="checkbox" class="rd-switch" role="switch" />
            {{ $t("show_all_days") }}
          </label>

          <ul v-if="days.length" class="rd-days">
            <li v-for="day in days" :key="day.key" class="rd-day">
              <span class="rd-day__tile" :class="day.tileClass">
                <b class="rd-num">{{ day.num }}</b>
                <span>{{ day.weekday }}</span>
              </span>
              <span>
                <template v-if="day.items.length">
                  <span v-for="item in day.items" :key="item.key" class="rd-day__meal">
                    {{ item.quantity }}× {{ item.name }}
                  </span>
                  <span class="rd-day__meta">{{ day.detail }}</span>
                </template>
                <span v-else class="rd-day__none">{{ day.emptyText }}</span>
              </span>
            </li>
          </ul>
          <div v-else class="rd-empty rd-empty--tight">
            <b>{{ $t("report_mobile.no_orders_month") }}</b>
          </div>
        </template>
      </div>
    </template>
  </div>
</template>

<script>
import moment from "moment";
import hasEmployeeMonth, { ISO } from "../../shared/hasEmployeeMonth";

/**
 * Radnik – mesečni pregled na računaru: brojke, mreža meseca (po danima) i spisak narudžbi.
 * Radnik dolazi kao prop (u listi radnika desno, u panelu iz "Preuzeto"); štampa se dugmetom,
 * a ne globalnom štampom, da se ne otvori dva puta pored liste radnika.
 */
export default {
  name: "ReportDesktopWorkerMonth",
  mixins: [hasEmployeeMonth],
  props: {
    worker: {
      type: Object,
      default: null,
    },
    /** U panelu naslov radnika već stoji u zaglavlju panela */
    compact: {
      type: Boolean,
      default: false,
    },
  },
  computed: {
    employee() {
      return this.worker;
    },
    handlesGlobalPrint() {
      return false;
    },
    initials() {
      return (this.employee?.fullName || "")
        .split(" ")
        .filter(Boolean)
        .map((part) => part.charAt(0))
        .slice(0, 2)
        .join("");
    },
    subtitle() {
      return [`ID ${this.employee.id}`, this.employee.companyName].filter(Boolean).join(" · ");
    },
    weekdays() {
      const formatter = new Intl.DateTimeFormat(this.formatLocale, { weekday: "narrow" });
      // 5. januar 2026. je ponedeljak
      return Array.from({ length: 7 }, (_, index) =>
        formatter.format(new Date(2026, 0, 5 + index)).toUpperCase()
      );
    },
    /** Mreža meseca: prazna polja do prvog dana (ponedeljak je prvi), pa dan po dan. */
    cells() {
      const offset = (this.monthStart.day() + 6) % 7;
      const today = moment().format(ISO);
      const list = Array.from({ length: offset }, () => null);
      for (let day = 1; day <= this.daysInMonth; day += 1) {
        const date = this.monthStart.clone().date(day);
        const key = date.format(ISO);
        const items = this.byDate[key] || [];
        const names = items.map((item) => item.name);
        list.push({
          key,
          day,
          text: names[0] ? (names.length > 1 ? `${names[0]} +${names.length - 1}` : names[0]) : "",
          title: items.map((item) => `${item.quantity}× ${item.name}`).join(", "),
          classes: {
            "is-ordered": items.length > 0,
            "is-today": key === today,
            "is-weekend": [0, 6].includes(date.day()),
          },
        });
      }
      return list;
    },
  },
};
</script>
